import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 10,
  },
  header: {
    backgroundColor: '#1565C0',
    color: '#ffffff',
    padding: 15,
    marginBottom: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerSubtitle: {
    fontSize: 12,
    opacity: 0.9,
  },
  headerBadge: {
    backgroundColor: '#D32F2F',
    color: '#ffffff',
    padding: '6 12',
    fontSize: 10,
    fontWeight: 'bold',
  },
  statsRow: {
    flexDirection: 'row',
    marginBottom: 20,
    gap: 10,
  },
  statBox: {
    flex: 1,
    border: '1px solid #e0e0e0',
    padding: 10,
    backgroundColor: '#f5f5f5',
  },
  statValue: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  statLabel: {
    fontSize: 8,
    color: '#666666',
  },
  table: {
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#e3f2fd',
    borderBottom: '2px solid #1565C0',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #e0e0e0',
  },
  tableRowAlt: {
    flexDirection: 'row',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
  },
  tableTotalRow: {
    flexDirection: 'row',
    backgroundColor: '#fff3e0',
    borderBottom: '2px solid #ff9800',
  },
  tableGrandTotal: {
    flexDirection: 'row',
    backgroundColor: '#ffcc80',
    borderBottom: '2px solid #e65100',
  },
  tableSectionHeader: {
    flexDirection: 'row',
    backgroundColor: '#e0e0e0',
    padding: '8 0',
  },
  cellNum: {
    width: 30,
    padding: 8,
    textAlign: 'center',
  },
  cellPallet: {
    width: 100,
    padding: 8,
  },
  cellKilos: {
    width: 70,
    padding: 8,
    textAlign: 'center',
    backgroundColor: '#e8f5e9',
  },
  cellLbs: {
    width: 70,
    padding: 8,
    textAlign: 'center',
    backgroundColor: '#e3f2fd',
  },
  cellDest: {
    width: 50,
    padding: 8,
    textAlign: 'center',
  },
  cellDesc: {
    flex: 1,
    padding: 8,
  },
  headerCell: {
    fontWeight: 'bold',
    fontSize: 9,
  },
  sectionTitle: {
    fontWeight: 'bold',
    fontSize: 11,
    padding: 8,
  },
  totalLabel: {
    fontWeight: 'bold',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    borderTop: '1px solid #e0e0e0',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#666666',
  },
});

const LoadSheetPDF = ({ vuelo, pallets, dangerousGoods }) => {
  const groupedPallets = {
    topside: pallets.filter((p) => !p.lower || p.lower === null),
    lower: pallets.filter((p) => p.lower === 2),
    bulk: pallets.filter((p) => p.lower === 3),
  };

  const totalKilos = pallets.reduce((sum, p) => sum + (parseFloat(p.pesoTotalP) || 0), 0);
  const totalLbs = Math.round(totalKilos * 2.20462);

  const topsideKilos = groupedPallets.topside.reduce((sum, p) => sum + (p.pesoTotalP || 0), 0);
  const topsideLbs = Math.round(topsideKilos * 2.20462);

  const renderPalletRow = (pallet, idx, isAlt) => {
    const lbs = Math.round((pallet.pesoTotalP || 0) * 2.20462);
    const hasDG = dangerousGoods?.[pallet.placa];
    const details = [];
    if (pallet.xl && pallet.xl !== 'N/A') details.push(pallet.xl);
    if (hasDG) {
      hasDG.forEach((dg) => details.push(`UN:${dg.un} C:${dg.clase}`));
    }
    if (pallet.notas2) details.push(pallet.notas2);

    return (
      <View key={pallet.placa} style={isAlt ? styles.tableRowAlt : styles.tableRow}>
        <Text style={styles.cellNum}>{idx + 1}</Text>
        <Text style={styles.cellPallet}>{pallet.placa}</Text>
        <Text style={styles.cellKilos}>{(pallet.pesoTotalP || 0).toLocaleString()}</Text>
        <Text style={styles.cellLbs}>{lbs.toLocaleString()}</Text>
        <Text style={styles.cellDest}>{vuelo?.Destino}</Text>
        <Text style={styles.cellDesc}>{details.join(', ')}</Text>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.headerTitle}>VUELO: {vuelo?.awb}</Text>
            <Text style={styles.headerSubtitle}>
              {vuelo?.equipo_nombre} | {vuelo?.Destino} | {vuelo?.fecha ? new Date(vuelo.fecha).toLocaleDateString() : ''}
            </Text>
          </View>
          {vuelo?.cerrado && (
            <View style={styles.headerBadge}>
              <Text>CERRADO</Text>
            </View>
          )}
        </View>

        {/* Stats */}
        <View style={styles.statsRow}>
          <View style={styles.statBox}>
            <Text style={styles.statValue}>{pallets.length}</Text>
            <Text style={styles.statLabel}>PALLETS</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statValue}>{totalKilos.toLocaleString()} kg</Text>
            <Text style={styles.statLabel}>PESO TOTAL</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statValue}>{totalLbs.toLocaleString()} lbs</Text>
            <Text style={styles.statLabel}>EN LIBRAS</Text>
          </View>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.cellNum, styles.headerCell]}>#</Text>
            <Text style={[styles.cellPallet, styles.headerCell]}>PALLET</Text>
            <Text style={[styles.cellKilos, styles.headerCell]}>KILOS</Text>
            <Text style={[styles.cellLbs, styles.headerCell]}>LBS</Text>
            <Text style={[styles.cellDest, styles.headerCell]}>DEST</Text>
            <Text style={[styles.cellDesc, styles.headerCell]}>DESCRIPCIÓN</Text>
          </View>

          {/* TOPSIDE Section */}
          <View style={styles.tableSectionHeader}>
            <Text style={styles.sectionTitle}>TOPSIDE</Text>
          </View>
          {groupedPallets.topside.map((pallet, idx) => renderPalletRow(pallet, idx, idx % 2 === 1))}
          
          {/* Topside Total */}
          <View style={styles.tableTotalRow}>
            <Text style={[styles.cellNum, styles.totalLabel]}></Text>
            <Text style={[styles.cellPallet, styles.totalLabel]}>TOPSIDE TOTAL</Text>
            <Text style={[styles.cellKilos, styles.totalLabel]}>{topsideKilos.toLocaleString()} Kg</Text>
            <Text style={[styles.cellLbs, styles.totalLabel]}>{topsideLbs.toLocaleString()} Lbs</Text>
            <Text style={styles.cellDest}></Text>
            <Text style={styles.cellDesc}></Text>
          </View>

          {/* LOWER DECK Section */}
          {groupedPallets.lower.length > 0 && (
            <>
              <View style={styles.tableSectionHeader}>
                <Text style={styles.sectionTitle}>LOWER DECK</Text>
              </View>
              {groupedPallets.lower.map((pallet, idx) => renderPalletRow(pallet, idx, idx % 2 === 1))}
            </>
          )}

          {/* BULK Section */}
          {groupedPallets.bulk.length > 0 && (
            <>
              <View style={styles.tableSectionHeader}>
                <Text style={styles.sectionTitle}>LOOSE BULK BELLY</Text>
              </View>
              {groupedPallets.bulk.map((pallet, idx) => renderPalletRow(pallet, idx, idx % 2 === 1))}
            </>
          )}

          {/* Grand Total */}
          <View style={styles.tableGrandTotal}>
            <Text style={[styles.cellNum, styles.totalLabel]}></Text>
            <Text style={[styles.cellPallet, styles.totalLabel]}>GRAND TOTAL</Text>
            <Text style={[styles.cellKilos, styles.totalLabel]}>{totalKilos.toLocaleString()} Kg</Text>
            <Text style={[styles.cellLbs, styles.totalLabel]}>{totalLbs.toLocaleString()} Lbs</Text>
            <Text style={styles.cellDest}></Text>
            <Text style={styles.cellDesc}></Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>Generado: {new Date().toLocaleString()}</Text>
          <Text>Pallet Control System - MIA-LATAM</Text>
        </View>
      </Page>
    </Document>
  );
};

export default LoadSheetPDF;

